import { useEffect, useState } from "react";
import api from "@/lib/api";
import { initPdfWorker, loadPdfDocument, getSafeErrorMessage } from "@/config/pdfConfig";
import { X, Download, Loader2, AlertCircle } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

/**
 * PdfTemplateViewer - in-app viewer for an uploaded PDF form template.
 * Opens instantly (never window.open, so no pop-up blocker) and renders pages with pdf.js,
 * which works consistently in Safari, Chrome and on tablets.
 */
export default function PdfTemplateViewer({ template, onClose }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    let url;
    let cancelled = false;

    (async () => {
      try {
        initPdfWorker();
        const { data } = await api.get(`/forms/pdf/${template.id}`, { responseType: "arraybuffer" });
        if (cancelled) return;

        url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        setDownloadUrl(url);

        const pdf = await loadPdfDocument(new Uint8Array(data).slice());
        const rendered = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.6 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          rendered.push({ dataUrl: canvas.toDataURL("image/png"), width: viewport.width, height: viewport.height });
        }
        if (!cancelled) setPages(rendered);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response
            ? "This PDF could not be loaded. Please try again or re-upload the file."
            : getSafeErrorMessage(err?.message));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [template.id]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const download = () => {
    if (!downloadUrl) return;
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = template.pdf_filename || `${template.name || "form"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1B1A]/70 backdrop-blur-sm flex flex-col" data-testid="pdf-template-viewer">
      <div className="bg-white px-4 sm:px-6 py-3 flex items-center gap-3 border-b border-[#1A1B1A]/[0.06]">
        <div className="min-w-0">
          <h2 className="text-base text-[#1A1B1A] truncate" style={H} data-testid="pdf-viewer-title">
            {template.name}
          </h2>
          <p className="text-xs text-[#757675] truncate">
            {template.pdf_filename || "Uploaded PDF form"}
            {pages.length ? ` · ${pages.length} page${pages.length > 1 ? "s" : ""}` : ""}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={download}
            disabled={!downloadUrl}
            data-testid="pdf-viewer-download-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs text-[#1A1B1A] hover:bg-[#FAF9F6] disabled:opacity-40 transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </button>
          <button
            onClick={onClose}
            data-testid="pdf-viewer-close-btn"
            className="p-2 rounded-xl border border-[#1A1B1A]/10 text-[#4A4B4A] hover:bg-[#FAF9F6] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {(template.applies_to_all || template.assigned_appointment_type_ids?.length > 0) && (
        <div className="bg-white px-4 sm:px-6 py-2 flex flex-wrap gap-1.5 border-b border-[#1A1B1A]/[0.04]">
          {template.applies_to_all && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-[#205B5A]/10 text-[#205B5A]">All appointments</span>
          )}
          {template.assigned_appointment_type_ids?.length > 0 && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-[#496275]/10 text-[#496275]">
              {template.assigned_appointment_type_ids.length} appointment type
              {template.assigned_appointment_type_ids.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      <div className="flex-1 overflow-auto bg-[#2A2B2A] p-3 sm:p-6">
        {loading ? (
          <div className="h-full flex items-center justify-center" data-testid="pdf-viewer-loading">
            <Loader2 className="w-6 h-6 text-white/70 animate-spin" />
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center">
            <div className="bg-white rounded-2xl px-6 py-5 max-w-sm text-center" data-testid="pdf-viewer-error">
              <AlertCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-[#4A4B4A]">{error}</p>
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-4" data-testid="pdf-viewer-pages">
            {pages.map((p, i) => (
              <img
                key={i}
                src={p.dataUrl}
                alt={`Page ${i + 1}`}
                data-testid={`pdf-viewer-page-${i + 1}`}
                className="w-full bg-white rounded-xl shadow-lg"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
